import { useState } from "react";
import useSchema from "../../../components/common/react-hooks/useCerokmSchema";
import AutocompleteMagic from "../../../components/common/editors/AutocompleteMagic";
import _ from "lodash";

function isValidPath(path) {
    return allPossiblePaths.includes(path);
}

const reservedFields = ['type', 'unit', '_comments', 'values', '_required'];

let allPossiblePaths = [];
let allPossiblePathsSchemaVersion;

function getAllPossiblePaths(schemaObj, currentPath = '', maxDepth = 100) {
    if (!schemaObj || maxDepth <= 0) return [];
    
    const paths = [];
    const stack = [[schemaObj, currentPath, 0]];
    while (stack.length > 0) {        
        const [current, path, depth] = stack.pop() || [];
        
        if (!current || depth >= maxDepth) continue;
        
        if (current.type === 'objectTypedKeys') {
            const keys = current.keys || [];
            for (const key of keys) {
                const newPath = path ? `${path}.${key}` : key;
                paths.push(newPath);
                if (current.valuesType?.properties) {
                    stack.push([current.valuesType.properties, newPath, depth + 1]);
                }
            }
        } else if (current.type === 'array') {
            if (current.items?.properties) {
                stack.push([current.items.properties, path, depth + 1]);
            }
        } else if (typeof current === 'object' && current !== null) {
            const keys = Object.keys(current).filter(k => !reservedFields.includes(k));
            for (const key of keys) {
                const newPath = path ? `${path}.${key}` : key;
                if(newPath.match(/values/)) {
                    debugger;
                }
                paths.push(newPath);
                const subObj = current[key]?.properties || current[key];
                if (subObj) {
                    stack.push([subObj, newPath, depth + 1]);
                }
            }

            if(current.type === 'unit') {
                paths.push(path+'.unit');
                paths.push(path+'.value');
            }
        } 
    }
    paths.push('powertrain.type'); // rule exception
    return paths;
}

const getAutocompleteOptions = (schema, value) => {
    if (!schema) {
        return [];
    }

    const pathParts = value.split('.');

    // Check if we have a complete path (ends with a complete key) or partial path (user is typing)
    // If the last part exists in schema, show next level. Otherwise, filter current level.
    let isCompletePath = false;
    let obj = schema;
    let currentLevel = [];
    let foundValidPath = false;

    // Navigate through the path to see if it's complete
    for (let i = 0; i < pathParts.length; i++) {
        const pathPart = pathParts[i];

        if (obj.type === 'objectTypedKeys') {
            if (obj.keys.includes(pathPart)) {
                obj = obj.valuesType?.properties;
                if (i === pathParts.length - 1) isCompletePath = true;
            } else {
                // Partial match at objectTypedKeys level
                currentLevel = obj.keys || [];
                foundValidPath = i < pathParts.length - 1 ? false : true;
                break;
            }
        } else if (obj.type === 'array') {
            obj = obj.items?.properties;
            if (i === pathParts.length - 1) isCompletePath = true;
        } else if (obj && obj[pathPart]) {
            obj = obj[pathPart]?.properties || obj[pathPart];
            if (i === pathParts.length - 1) isCompletePath = true;
        } else {
            // Partial match - get available keys at current level
            if (typeof obj === 'object' && obj !== null) {
                currentLevel = _.without(Object.keys(obj), ... reservedFields);
            }
            foundValidPath = i < pathParts.length - 1 ? false : true;
            break;
        }
    }

    if (isCompletePath && obj) {
        // Show next level options
        if (obj.type === 'objectTypedKeys') {
            currentLevel = obj.keys || [];
        } else if (obj.type === 'array') {
            return []; // Can't go deeper into arrays
        } else if (typeof obj === 'object' && obj !== null) {
            currentLevel = _.without(Object.keys(obj), ... reservedFields);
        }

        // For complete paths, show all next level options with full path
        const basePath = value + '.';
        return [value].concat(currentLevel.map((key) => basePath + key));
    } else {
        // Filter current level based on partial input
        const lastPart = pathParts[pathParts.length - 1];
        const pathWithoutLast = pathParts.slice(0, -1);
        const filteredKeys = currentLevel.filter((key) => key.toLowerCase().startsWith(lastPart.toLowerCase()));

        // If no matches found and path seems broken, do global search
        if (filteredKeys.length === 0 && value.length > 1) {
            const paths = allPossiblePaths;
            // For performance, thousands of operations on a proxy gets slow
            let s = value.toLowerCase();
            return paths.filter((path) => path.toLowerCase().indexOf(s) !== -1).slice(0, 50);
        }

        const basePath = pathWithoutLast.length > 0 ? pathWithoutLast.join('.') + '.' : '';
        return filteredKeys.map((key) => basePath + key);
    }
};

export default function SchemaPathInput({ value, onChange, ...otherProps }) {
    const schemaDoc = useSchema();
    
    if(schemaDoc &&allPossiblePathsSchemaVersion !== schemaDoc.version) {
        allPossiblePaths = getAllPossiblePaths(schemaDoc.schema.properties);
        allPossiblePathsSchemaVersion = schemaDoc.version;
    }
    
    const isValid = schemaDoc && (value === '$currentFullPath' || isValidPath(value));
    const backgroundClass = 'form-control rounded p-1 ' + (schemaDoc ? (isValid ? 'bg-light-partial-success' : 'bg-light-danger') : 'bg-light-secondary');
    
    const getOptions = (text) => {
        text = text || '';
        if(!schemaDoc) {
            return [];
        }

        const res = getAutocompleteOptions(schemaDoc.schema.properties, text).map(key => ({
            label: key,
            value: key
        }));
        // console.log("getOptions", text, res);
        return res;
    }

    return <div className="d-flex align-self-stretch bg-white rounded" style={{}}>
        <AutocompleteMagic 
            className={backgroundClass}
            style={{ height: '100%', fontSize: '13px', minWidth: '350px' }}
            placeholder="e.g: commercial.price.baseMSRP"
            text={value}
            autoSize={false}
            startClosed={true}
            onChange={(v) => onChange(v)}
            onCancel={() => false}
            onGetOptions={getOptions}
            {...otherProps}
        />
    </div>
}